import React, { useEffect, useRef, useState } from 'react';
import TickBox from '../components/TickBox';
import YouTubePlayer from '../components/YouTubePlayer';
import { usePlaylistVideos } from '../hooks/usePlaylistVideos';
import { listIdOf, splitMusicFields } from './musicLayout';
import { useCreativePreferencesStore } from '../state/store';
const has = (list, id) => (list || []).some(x => String(x) === String(id));

const closeGlyph = () => React.createElement("svg", {
  width: "18",
  height: "18",
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  strokeLinecap: "round"
}, React.createElement("path", {
  d: "M18 6L6 18M6 6l12 12"
}));

// A <select> hands its open list to the browser, which draws it in OS chrome
// that no stylesheet here can reach. This is the same control in the form's
// own skin.
const LangMenu = ({
  choices,
  value,
  placeholder,
  disabled,
  label,
  onPick
}) => {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = e => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = e => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const current = choices.find(c => String(c.id) === String(value));
  return React.createElement("div", {
    className: "cp-langmenu",
    ref: boxRef
  }, React.createElement("button", {
    type: "button",
    className: `cp-langselect${open ? ' is-open' : ''}`,
    "aria-label": label,
    "aria-haspopup": "listbox",
    "aria-expanded": open,
    disabled: disabled,
    onClick: () => setOpen(!open)
  }, current ? current.name : placeholder), open && React.createElement("div", {
    className: "cp-langlist",
    role: "listbox"
  }, choices.map(choice => React.createElement("button", {
    key: choice.id,
    type: "button",
    role: "option",
    "aria-selected": String(choice.id) === String(value),
    className: `cp-langopt${String(choice.id) === String(value) ? ' is-on' : ''}`,
    onClick: () => {
      setOpen(false);
      onPick(choice.id);
    }
  }, choice.name))));
};

// The category is the value. Children are only the playlists behind it —
// Hindi and English are two views of Romantic, not two things to pick.

// The variant's own playlist and its tracks. A centred modal: the language
// switch sits above the list so the client can compare before deciding.
const SongDrawer = ({
  card,
  variant,
  selected,
  locked,
  onPick,
  onSetVariant,
  onClose
}) => {
  const {
    ids,
    titles
  } = usePlaylistVideos(listIdOf(variant));
  const [playing, setPlaying] = useState(null);
  const children = card.children || [];
  useEffect(() => {
    const onKey = e => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);
  const subtitle = variant.id === card.id ? 'Playlist' : `${variant.name} playlist`;
  return React.createElement("div", {
    className: "cp-pickerscrim",
    onClick: onClose
  }, React.createElement("div", {
    className: "cp-picker",
    onClick: e => e.stopPropagation()
  }, React.createElement("div", {
    className: "cp-picker-head"
  }, React.createElement("div", null, React.createElement("p", {
    className: "cp-eyebrow"
  }, "Playlist"), React.createElement("p", {
    className: "cp-picker-name"
  }, card.name), React.createElement("p", {
    className: "cp-picker-count"
  }, ids.length ? `${ids.length} songs · tap play to listen` : 'Loading songs…')), React.createElement("button", {
    type: "button",
    className: "cp-picker-x",
    onClick: onClose,
    "aria-label": "Close"
  }, closeGlyph())), children.length > 1 && React.createElement("div", {
    className: "cp-picker-filters"
  }, children.map(child => React.createElement("button", {
    key: child.id,
    type: "button",
    className: `cp-filterchip${child.id === variant.id ? ' is-on' : ''}`,
    "aria-pressed": child.id === variant.id,
    onClick: () => {
      setPlaying(null);
      onSetVariant(child);
    }
  }, child.name))), React.createElement("div", {
    className: "cp-picker-list"
  }, ids.length ? ids.slice(0, 30).map((id, i) => React.createElement("div", {
    className: "cp-songrow",
    key: id
  }, React.createElement("button", {
    type: "button",
    className: `cp-songplay${playing === id ? ' is-on' : ''}`,
    onClick: () => setPlaying(playing === id ? null : id),
    "aria-label": `${playing === id ? 'Pause' : 'Play'} track ${i + 1}`
  }, playing === id ? React.createElement("svg", {
    width: "9",
    height: "9",
    viewBox: "0 0 24 24",
    fill: "#FFFFFF"
  }, React.createElement("path", {
    d: "M7 5h4v14H7zM13 5h4v14h-4z"
  })) : React.createElement("svg", {
    width: "9",
    height: "9",
    viewBox: "0 0 24 24",
    fill: "#8C6244"
  }, React.createElement("path", {
    d: "M8 5l12 7-12 7z"
  }))), React.createElement("button", {
    type: "button",
    className: "cp-songmeta",
    onClick: () => setPlaying(playing === id ? null : id)
  }, React.createElement("span", {
    className: "cp-songtitle"
  }, titles[id] || `Track ${i + 1}`), React.createElement("span", {
    className: "cp-songsub"
  }, subtitle)))) : React.createElement("p", {
    className: "cp-emptymedia"
  }, "Loading songs from the playlist…")), playing && React.createElement("div", {
    className: "cp-nowbar"
  }, React.createElement("div", {
    className: "cp-nowplayer"
  }, React.createElement(YouTubePlayer, {
    videoId: playing,
    title: card.name,
    autoplay: true
  })), React.createElement("div", {
    className: "cp-nowinfo"
  }, React.createElement("div", {
    className: "cp-nowmeta"
  }, React.createElement("p", {
    className: "cp-eyebrow"
  }, "Now playing"), React.createElement("p", {
    className: "cp-nowtitle"
  }, titles[playing] || 'Track')), React.createElement("button", {
    type: "button",
    className: "cp-nowclose",
    onClick: () => setPlaying(null),
    "aria-label": "Close player"
  }, closeGlyph()))), React.createElement("div", {
    className: "cp-picker-foot"
  }, React.createElement("button", {
    type: "button",
    className: `cp-pickerselect${selected ? ' is-on' : ''}`,
    disabled: locked,
    onClick: onPick
  }, selected ? `${card.name} selected` : `Select ${card.name}`), React.createElement("button", {
    type: "button",
    className: "cp-ghost",
    onClick: onClose
  }, "Close"))));
};

/**
 * Music: cards that group language variants. The card is a header, not a
 * value — every stored id is a child's, so Hindi and English of one category
 * are two picks against the cap.
 */
const MusicSection = ({
  section,
  group,
  locked,
  onToggle,
  onText
}) => {
  const {
    cards,
    linkOf,
    chips,
    noteOf
  } = splitMusicFields(section);
  const {
    cpAwaitingChoice,
    setCpAwaitingChoice
  } = useCreativePreferencesStore();
  const [open, setOpen] = useState(null);
  const picked = group[cards.key] || [];
  const cap = cards.max_picks || 0;
  const atLimit = cap > 0 && picked.length >= cap;
  const isOn = id => has(picked, id);
  // Answers saved before the category became the value may still hold a
  // child id, so the card still reads as selected and clears cleanly.
  const cardOn = option => isOn(option.id) || (option.children || []).some(c => isOn(c.id));
  const toggleKey = id => onToggle(cards.key, id, cap);

  // Ticking a card with languages stores the category, which on its own reads
  // as "the whole playlist". The flag is what tells the two apart, so the
  // bottom bar can hold the client here until they say which one they meant.
  const awaiting = id => !!cpAwaitingChoice[id];
  const setAwaiting = (id, on) => {
    if (awaiting(id) === on) return;
    const next = {
      ...cpAwaitingChoice
    };
    if (on) next[id] = true;else delete next[id];
    setCpAwaitingChoice(next);
  };
  const toggleCard = option => {
    if (!cardOn(option)) {
      toggleKey(option.id);
      if ((option.children || []).length > 1) setAwaiting(option.id, true);
      return;
    }
    if (isOn(option.id)) toggleKey(option.id);
    (option.children || []).filter(c => isOn(c.id)).forEach(c => toggleKey(c.id));
    setAwaiting(option.id, false);
  };

  // One answer per category: the whole playlist, or one of its languages.
  // The old value is cleared before the new one is stored, so switching
  // between them never trips the cap the way two adds in a row would.
  const chooseWithin = (option, id) => {
    setAwaiting(option.id, false);
    if (isOn(id)) return;
    if (isOn(option.id)) toggleKey(option.id);
    (option.children || []).filter(c => isOn(c.id)).forEach(c => toggleKey(c.id));
    toggleKey(id);
  };

  // Empty until the choice is made, so the select shows its prompt rather
  // than a value the client never picked.
  const chosenWithin = option => {
    if (awaiting(option.id)) return '';
    const child = (option.children || []).find(c => isOn(c.id));
    return String(child ? child.id : option.id);
  };

  // A pasted link implies the category; clearing it releases the implication.
  const onLink = (option, field, value) => {
    onText(field.key, value);
    const wanted = !!value.trim();
    if (wanted !== isOn(option.id)) toggleKey(option.id);
  };
  return React.createElement(React.Fragment, null, React.createElement("div", {
    className: "cp-musicgrid"
  }, (cards.options || []).map(option => {
    const children = option.children || [];
    const link = linkOf[option.id];
    const on = cardOn(option);
    const blocked = locked || atLimit && !on;
    return React.createElement("div", {
      key: option.id,
      className: `cp-music${on ? ' is-on' : ''}${locked && !on ? ' is-locked' : ''}`
    }, React.createElement("button", {
      type: "button",
      className: "cp-music-head",
      "aria-pressed": on,
      disabled: blocked || !!link,
      onClick: () => toggleCard(option)
    }, !link && React.createElement(TickBox, {
      checked: on,
      size: 20
    }), React.createElement("span", {
      className: "cp-music-titles"
    }, React.createElement("span", {
      className: "cp-music-name"
    }, option.name), option.blurb && React.createElement("span", {
      className: "cp-music-blurb"
    }, option.blurb))), link ? React.createElement("div", null, React.createElement("p", {
      className: "cp-fieldlabel"
    }, link.label), React.createElement("input", {
      type: "url",
      className: "cp-music-field",
      "aria-label": link.label,
      value: group[link.key] || '',
      disabled: locked,
      onChange: e => onLink(option, link, e.target.value)
    })) : React.createElement("div", {
      className: "cp-music-status"
    }, React.createElement("span", null, children.length ? `${children.length} language playlists` : 'Reference playlist'), React.createElement("button", {
      type: "button",
      className: "cp-viewplaylist",
      onClick: () => setOpen({
        card: option,
        variant: children[0] || option
      })
    }, "View playlist")),
    // A category with languages asks which one. The whole playlist stays an
    // answer in its own right, so a couple who does not mind can say so.
    on && children.length > 1 && React.createElement("div", {
      className: "cp-langpick"
    }, React.createElement("span", {
      className: "cp-langpick-label"
    }, "Your choice:"), React.createElement("span", {
      className: `cp-langhalo${awaiting(option.id) ? ' is-missing' : ''}`
    }, React.createElement(LangMenu, {
      label: `${option.name} playlist`,
      choices: [{
        id: option.id,
        name: 'Whole playlist'
      }].concat(children),
      value: chosenWithin(option),
      placeholder: "Select one",
      disabled: locked,
      onPick: id => chooseWithin(option, id)
    }))));
  })), chips.map(chip => {
    const chosen = group[chip.key] || [];
    const note = noteOf[chip.key];
    return React.createElement("div", {
      className: "cp-avoid",
      key: chip.key
    }, chip.label && React.createElement("p", {
      className: "cp-music-name"
    }, chip.label), React.createElement("div", {
      className: "cp-chips"
    }, (chip.options || []).map(option => React.createElement("button", {
      key: option.id,
      type: "button",
      className: `cp-chip${has(chosen, option.id) ? ' is-on' : ''}`,
      "aria-pressed": has(chosen, option.id),
      disabled: locked,
      onClick: () => onToggle(chip.key, option.id, chip.max_picks)
    }, option.name))), note && chosen.length > 0 && React.createElement(React.Fragment, null, React.createElement("p", {
      className: "cp-fieldlabel"
    }, note.label), React.createElement("textarea", {
      className: "cp-avoid-text",
      rows: 3,
      "aria-label": note.label,
      value: group[note.key] || '',
      disabled: locked,
      onChange: e => onText(note.key, e.target.value)
    })));
  }), open && React.createElement(SongDrawer, {
    card: {
      ...open.card,
      children: open.card.children || []
    },
    variant: open.variant,
    selected: cardOn(open.card),
    locked: locked || atLimit && !cardOn(open.card),
    onPick: () => toggleCard(open.card),
    onSetVariant: child => setOpen({
      card: open.card,
      variant: child
    }),
    onClose: () => setOpen(null)
  }));
};
export default MusicSection;
